import React, { useState, useRef } from "react";
import { Link } from "react-router";
import { Button, Input, Select, Messages } from "../components/ui";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import AuthService from "../services/AuthService";

function SolicitarRecuperacaoSenha() {
    const [email, setEmail] = useState("");
    const [tipoUsuario, setTipoUsuario] = useState({ name: "Atleta / Corredor", value: "Corredor" });
    const [loading, setLoading] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const msg = useRef(null);

    const authService = new AuthService();

    const tipoOptions = [
        { name: "Atleta / Corredor", value: "Corredor" },
        { name: "Organizador", value: "Organizador" }
    ];

    const showMessage = (severity, summary, detail) => {
        if (!msg.current) return;
        msg.current.clear();
        msg.current.show([{ severity, summary, detail }]);
    };

    const getTipoUsuarioValue = (val) => {
        if (!val) return "Corredor";
        if (typeof val === "object") {
            if (val.value) return String(val.value);
            if (val.name) {
                return val.name.toLowerCase().includes("organiz") ? "Organizador" : "Corredor";
            }
        }
        if (typeof val === "string") {
            const lower = val.toLowerCase();
            if (lower.includes("organiz")) return "Organizador";
            return "Corredor";
        }
        return "Corredor";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!email) {
            showMessage("warn", "Atenção", "Por favor, informe seu e-mail.");
            return;
        }

        setLoading(true);
        showMessage("info", "Aguarde", "Enviando solicitação...");

        try {
            const payload = JSON.stringify({
                email: email.trim(),
                tipo_usuario: getTipoUsuarioValue(tipoUsuario)
            });

            const response = await authService.requestPasswordReset(payload);
            const data = await response.json();

            if (response.ok) {
                setEnviado(true);
                showMessage(
                    "success",
                    "E-mail Enviado!",
                    data.detalhe || "Se o e-mail informado estiver cadastrado, um e-mail com instruções para redefinição de senha foi enviado."
                );
            } else {
                const errorMsg =
                    data?.email?.[0] ||
                    data?.erro ||
                    data?.detalhe ||
                    "Ocorreu um erro ao processar sua solicitação. Tente novamente.";
                showMessage("error", "Erro!", errorMsg);
            }
        } catch (error) {
            showMessage("error", "Erro!", "Não foi possível conectar ao servidor. Tente novamente mais tarde.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="landing-page-wrapper min-h-screen flex flex-col justify-between bg-urb-dark-login">
            <LandingHeader />

            <main className="flex-1 flex flex-col items-center justify-center py-10 px-4">
                {/* Header Title */}
                <div className="text-center mb-6">
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase m-0 flex items-center justify-center gap-1.5">
                        <span className="text-white">URB</span>
                        <span className="text-urb-primary">RACE</span>
                    </h1>
                    <p className="text-sm md:text-base mt-2 m-0 text-urb-text-secondary">
                        Recuperação de Senha
                    </p>
                </div>

                {/* Card Container */}
                <div className="w-full max-w-md rounded-2xl border border-urb-border p-6 sm:p-8 shadow-2xl bg-urb-card/85 backdrop-blur-md">
                    <Messages ref={msg} />

                    {!enviado ? (
                        <form onSubmit={handleSubmit} className="flex flex-col w-full">
                            <p className="text-sm text-urb-text-secondary mb-5 text-center leading-relaxed">
                                Digite seu e-mail cadastrado e selecione o tipo de conta para receber o link de redefinição de senha.
                            </p>

                            {/* Tipo de Usuário Dropdown */}
                            <div className="flex flex-col gap-2 mb-4 w-full text-left">
                                <label htmlFor="tipo-usuario" className="text-sm font-semibold text-white">
                                    Tipo de Conta
                                </label>
                                <Select
                                    id="tipo-usuario"
                                    value={tipoUsuario}
                                    onChange={(e) => setTipoUsuario(e.value)}
                                    options={tipoOptions}
                                    optionLabel="name"
                                    placeholder="Selecione o tipo de conta"
                                    disabled={loading}
                                />
                            </div>

                            {/* Email Input */}
                            <div className="flex flex-col gap-2 mb-5 w-full text-left">
                                <label htmlFor="email" className="text-sm font-semibold text-white">
                                    E-mail
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="seu.email@exemplo.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                label={loading ? "ENVIANDO..." : "ENVIAR LINK DE REDEFINIÇÃO"}
                                variant="primary"
                                size="lg"
                                className="w-full uppercase mb-4 shadow-lg"
                                loading={loading}
                                disabled={loading}
                            />

                            {/* Voltar para o Login */}
                            <div className="text-center mt-2">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-1.5 text-xs text-urb-text-secondary hover:text-white hover:underline transition-colors"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                    Voltar para o Login
                                </Link>
                            </div>
                        </form>
                    ) : (
                        <div className="flex flex-col items-center text-center py-4">
                            <CheckCircle2 className="w-14 h-14 text-urb-primary mb-3" />
                            <h3 className="text-xl font-bold text-white mb-2">Instruções Enviadas!</h3>
                            <p className="text-sm text-urb-text-secondary mb-6 leading-relaxed">
                                Verifique sua caixa de entrada (e a pasta de spam) para dar continuidade à redefinição de senha.
                            </p>
                            <Link to="/login" className="w-full">
                                <Button
                                    label="IR PARA O LOGIN"
                                    variant="primary"
                                    size="lg"
                                    className="w-full uppercase"
                                />
                            </Link>
                        </div>
                    )}
                </div>
            </main>

            <LandingFooter />
        </div>
    );
}

export default SolicitarRecuperacaoSenha;
